import { Box, Chip, Stack, ToggleButton, ToggleButtonGroup, Toolbar, useTheme } from '@mui/material';
import { GridToolbarProps, ToolbarPropsOverrides } from '@mui/x-data-grid';
import { map } from 'lodash';
import React from 'react';

import ActionsMenu from 'components/ActionsMenu';
import { useRowSelectionContext } from 'components/atoms/RowSelectionContext';
import { typeToName } from 'components/ReviewPanel/form.util';
import { Permission } from 'interfaces/permissionOption';
import { ProceduresFieldsCategory } from 'interfaces/procedure/fields';
import { ReviewForm } from 'interfaces/reviewForm';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { ExperimentResultsSelection } from 'utils/useEncodedFilters/types';
import { usePermissions } from 'utils/usePermissions';
import { ProcedurePagination } from '../ProcedurePagination';
import { BulkEditControl } from './BulkEditControl';
import SelectedSlidesCopyButton from '../SelectedSlidesCopyButton';

const COMPACT_ACTION_BUTTONS_WIDTH = 1400;

export interface DataGridHeaderProps extends GridToolbarProps, ToolbarPropsOverrides {
  bulkEditMode: boolean;
  totalSlides: number;
  totalCases: number;
  isLoading: boolean;
  currentStains?: string[];
  disableEditing: boolean;
  selectedTableColumnGroup: ProceduresFieldsCategory[];
  setSelectedTableColumnGroup?: React.Dispatch<React.SetStateAction<ProceduresFieldsCategory[]>>;
  onBulkEditSave?: (changes: { [field: string]: any }) => void;
  forms?: ReviewForm[];
  hideActionsMenu?: boolean;
  hidePagination?: boolean;
  onPageChange?: (withOffset: boolean) => (event: React.ChangeEvent<unknown>, nextPage?: number) => void;
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  forcePage?: number;
  forcePageSize?: number;
  containerWidth?: number;
}

export const DataGridHeader: React.FC<React.PropsWithChildren<DataGridHeaderProps>> = ({
  bulkEditMode,
  totalCases,
  totalSlides,
  currentStains,
  isLoading,
  disableEditing,
  selectedTableColumnGroup,
  setSelectedTableColumnGroup,
  onBulkEditSave,
  forms,
  hideActionsMenu = false,
  hidePagination = false,
  onPageChange,
  onRowsPerPageChange,
  forcePage,
  forcePageSize,
  containerWidth,
}) => {
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  const canViewClinicalData = hasPermission(Permission.ViewClinicalData);

  const { noneSelected, omittedRows, selectedRows, selectedRowsCount, selectionMode } = useRowSelectionContext();
  const {
    queryParams: { slidesMode },
  } = useEncodedFilters({
    experimentResultsSelection: ExperimentResultsSelection.OnlyQAFailed,
  });

  const totalRows = slidesMode ? totalSlides : totalCases;
  const noRows = totalRows === 0;

  const disableRowOperations = isLoading || noRows || noneSelected(totalRows);
  const selectedRowCount = selectedRowsCount(totalRows);
  const compactActionButtons = Boolean(containerWidth && containerWidth < COMPACT_ACTION_BUTTONS_WIDTH);
  const selectionSummary = compactActionButtons
    ? `${selectedRowCount} selected`
    : `${selectedRowCount} / ${totalRows} ${slidesMode ? 'slide' : 'case'}${totalRows === 1 ? '' : 's'} selected`;
  const editingSummary = `Editing ${selectedRowCount} / ${totalRows} ${slidesMode ? 'slide' : 'case'}${
    totalRows === 1 ? '' : 's'
  }`;
  const statusChipLabel = bulkEditMode ? editingSummary : selectionSummary;

  return (
    <Toolbar
      variant="dense"
      sx={{
        backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800],
        justifyContent: 'space-between',
        position: 'relative',
        gap: 1,
        flexWrap: 'nowrap',
        overflowX: compactActionButtons ? 'auto' : 'visible',
        scrollbarWidth: compactActionButtons ? 'thin' : undefined,
        '&::-webkit-scrollbar': {
          height: compactActionButtons ? 6 : 0,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flex: '1 1 auto',
          minWidth: 0,
          width: 'auto',
        }}
      >
        <Box
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            overflowX: 'auto',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              height: 6,
            },
          }}
        >
          <ToggleButtonGroup
            size="small"
            color="primary"
            value={selectedTableColumnGroup}
            onChange={(_, newSelectedTableColumnGroup) =>
              setSelectedTableColumnGroup?.(newSelectedTableColumnGroup as ProceduresFieldsCategory[])
            }
            aria-label="table-column-groups"
            sx={{ whiteSpace: 'nowrap' }}
          >
            <ToggleButton value="labels">Labels</ToggleButton>
            <ToggleButton value="metadata">Metadata</ToggleButton>
            {canViewClinicalData && <ToggleButton value="patient">Patient</ToggleButton>}
            {canViewClinicalData && <ToggleButton value="clinical">Clinical</ToggleButton>}
            {canViewClinicalData && <ToggleButton value="outcome">Outcome</ToggleButton>}
            {map(forms, (form) => (
              <ToggleButton key={form.type} value={form.type}>
                {typeToName(form.type)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        {!hidePagination && (
          <Box sx={{ flex: '0 0 auto' }}>
            <ProcedurePagination
              totalSlides={totalSlides}
              dataGridPagination
              hideRowsPerPage
              totalCases={totalCases}
              isLoading={isLoading}
              onPageChange={onPageChange}
              onRowsPerPageChange={onRowsPerPageChange}
              forcePage={forcePage}
              forcePageSize={forcePageSize}
            />
          </Box>
        )}
      </Box>

      {selectedRowCount > 0 && (
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{
            position: compactActionButtons ? 'static' : 'absolute',
            left: compactActionButtons ? 'auto' : '50%',
            transform: compactActionButtons ? 'none' : 'translateX(-50%)',
            order: 0,
            flex: compactActionButtons ? '0 1 auto' : undefined,
            minWidth: 0,
            maxWidth: '100%',
          }}
        >
          <Chip
            color={bulkEditMode ? 'warning' : 'primary'}
            variant="filled"
            data-testid="study-dashboard-selection-summary"
            sx={{
              minWidth: 0,
              maxWidth: '100%',
              fontWeight: 600,
              '& .MuiChip-label': {
                px: 1.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
            label={statusChipLabel}
          />
          {slidesMode ? (
            <SelectedSlidesCopyButton
              selectedRows={selectedRows}
              omittedRows={omittedRows}
              selectionMode={selectionMode}
              color={bulkEditMode ? 'warning' : 'primary'}
            />
          ) : null}
        </Stack>
      )}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={1}
        sx={{
          flex: '0 0 auto',
          minWidth: 0,
          width: 'auto',
          marginInlineStart: 'auto',
          flexWrap: 'nowrap',
          rowGap: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'nowrap', flex: '0 0 auto' }}>
          {!disableEditing && (
            <BulkEditControl disabled={disableRowOperations} onSave={onBulkEditSave} compact={compactActionButtons} />
          )}

          {!bulkEditMode && !hideActionsMenu && (
            <ActionsMenu
              totalRows={totalRows}
              currentStains={currentStains}
              disablePortal={false}
              compact={compactActionButtons}
            />
          )}
        </Stack>
      </Stack>
    </Toolbar>
  );
};

export const DataGridFooter: React.FC<
  React.PropsWithChildren<{
    totalSlides: number;
    totalCases: number;
    isLoading?: boolean;
    useDataGridPagination?: boolean;
    onPageChange?: (withOffset: boolean) => (event: React.ChangeEvent<unknown>, nextPage?: number) => void;
    onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    forcePage?: number;
    forcePageSize?: number;
  }>
> = ({
  totalSlides,
  totalCases,
  isLoading = false,
  useDataGridPagination = true,
  onPageChange,
  onRowsPerPageChange,
  forcePage,
  forcePageSize,
}) => {
  const theme = useTheme();
  return (
    <Toolbar
      variant="dense"
      sx={{
        '&.MuiToolbar-root': {
          paddingLeft: 0,
          paddingRight: 0,
          backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800],
          justifyContent: 'flex-end',
          overflowX: 'auto',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            height: 6,
          },
          '& .MuiTablePagination-root': {
            width: 'max-content',
            flexShrink: 0,
          },
        },
      }}
    >
      <ProcedurePagination
        totalSlides={totalSlides}
        alignRowsPerPageLeft
        dataGridPagination={useDataGridPagination}
        showRowsPerPageSelector={!useDataGridPagination}
        totalCases={totalCases}
        isLoading={isLoading}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        forcePage={forcePage}
        forcePageSize={forcePageSize}
      />
    </Toolbar>
  );
};
